import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import type { UseMutationResult } from "@tanstack/react-query";

interface RegionsTabProps {
  effectiveClientId: string | null;
  createRegionMutation: UseMutationResult<
    unknown,
    unknown,
    { name: string; is_active: boolean }
  >;
  updateRegionMutation: UseMutationResult<
    unknown,
    unknown,
    { regionId: string; payload: { name?: string; is_active?: boolean } }
  >;
  deleteRegionMutation: UseMutationResult<unknown, unknown, string>;
  regionsLoading: boolean;
  regionsList: { id: string; name: string; is_active?: boolean }[];
}

export const RegionsTab: React.FC<RegionsTabProps> = ({
  effectiveClientId,
  createRegionMutation,
  updateRegionMutation,
  deleteRegionMutation,
  regionsLoading,
  regionsList,
}) => {
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addActive, setAddActive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editActive, setEditActive] = useState(true);

  const filtered = regionsList.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenAdd = () => {
    setAddName("");
    setAddActive(true);
    setAddOpen(true);
  };

  const handleConfirmAdd = () => {
    if (!addName.trim() || !effectiveClientId) return;
    createRegionMutation.mutate(
      { name: addName.trim(), is_active: addActive },
      { onSuccess: () => setAddOpen(false) }
    );
  };

  const handleStartEdit = (region: {
    id: string;
    name: string;
    is_active?: boolean;
  }) => {
    setEditingId(region.id);
    setEditName(region.name);
    setEditActive(region.is_active !== false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleSaveEdit = (id: string) => {
    updateRegionMutation.mutate(
      { regionId: id, payload: { name: editName, is_active: editActive } },
      { onSuccess: () => handleCancelEdit() }
    );
  };

  return (
    <SettingsSection>
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage regions.</Alert>
      )}

      <SectionCard
        title="Regions"
        description={`${regionsList.length} region${regionsList.length === 1 ? "" : "s"} configured`}
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            disabled={!effectiveClientId}
            onClick={handleOpenAdd}
          >
            Add region
          </Button>
        }
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search regions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        <Divider />

        {regionsLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading regions…
            </Typography>
          </Stack>
        ) : regionsList.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <PublicIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">No regions yet.</Typography>
            <Typography variant="caption">
              Click "Add region" to create one.
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No regions match "{search}"
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {filtered.map((region) => {
              const isEditing = editingId === region.id;
              const isActive = region.is_active !== false;

              return (
                <Box key={region.id} sx={{ py: 1.25 }}>
                  {isEditing ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editName.trim())
                            handleSaveEdit(region.id);
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Chip
                        label={editActive ? "Active" : "Inactive"}
                        size="small"
                        color={editActive ? "success" : "default"}
                        variant={editActive ? "filled" : "outlined"}
                        onClick={() => setEditActive((v) => !v)}
                        sx={{ cursor: "pointer" }}
                      />
                      <Tooltip title="Save">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={
                              !editName.trim() || updateRegionMutation.isPending
                            }
                            onClick={() => handleSaveEdit(region.id)}
                          >
                            {updateRegionMutation.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {region.name}
                      </Typography>
                      <Chip
                        label={isActive ? "Active" : "Inactive"}
                        size="small"
                        color={isActive ? "success" : "default"}
                        variant="outlined"
                      />
                      <Tooltip title="Rename">
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(region)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleteRegionMutation.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                "Delete this region? This cannot be undone."
                              )
                            ) {
                              deleteRegionMutation.mutate(region.id);
                            }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add region</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Region name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && addName.trim()) handleConfirmAdd();
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={addActive}
                  onChange={(e) => setAddActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!addName.trim() || createRegionMutation.isPending}
            onClick={handleConfirmAdd}
          >
            {createRegionMutation.isPending ? (
              <CircularProgress size={18} />
            ) : (
              "Add region"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};
